import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ComplianceAdminService } from '../../../_core/_services/_compliance/compliance-admin.service';
import { NotificationService } from '../../../_core/_services/notification.service';
import { finalize, forkJoin } from 'rxjs';
import { ApiMeta } from '../../../_core/_model/api-response';

@Component({
  selector: 'app-admin-tiers-list',
  standalone: false,
  templateUrl: './admin-tiers-list.html',
  styleUrl: './admin-tiers-list.css',
})
export class AdminTiersList implements OnInit {
  private readonly filterStorageKey = 'admin-compliance-tiers-filter';

  meta: ApiMeta | null = null;

  url: any;

  loading = false;
  lookupsLoading = false;
  error: string | null = null;
  items: any[] = [];
  jurisdictions: any[] = [];

  page: number = 1;

  defaultFilter = {
    jurisdictionId: '',
    customerType: '',
    tierCode: '',
    isActive: '',
    page: this.page,
    pageSize: ''
  };

  filter = { ...this.defaultFilter };

  customerTypes = [
    { label: 'Individual', value: 'Individual' },
    { label: 'Business', value: 'Business' }
  ];

  constructor(
    private adminService: ComplianceAdminService,
    private router: Router,
    private notifyService: NotificationService,
  ) {
    this.url = this.router.url.slice(1);
  }

  ngOnInit(): void {
    this.restoreFilters();
    this.loadLookups();
  }

  onPageLoad(page:number) {
    this.page = page;
    this.filter.page = page;
    this.restoreFilters();
    this.loadLookups();
  }

  loadLookups(): void {
    this.lookupsLoading = true;
    this.error = null;

    forkJoin({
      jurisdictions: this.adminService.getJurisdictions({ page: 1, pageSize: 500 })
    })
      .pipe(finalize(() => (this.lookupsLoading = false)))
      .subscribe({
        next: (res: any) => {
          this.meta = res?.meta;
          this.jurisdictions = res?.jurisdictions?.data || res?.jurisdictions || [];
          this.load();
        },
        error: (err) => {
          this.error = err?.error?.message || 'Failed to load lookup data.';
        }
      });
  }

  load(): void {
    this.loading = true;
    this.error = null;

    const payload: any = {
      page: this.filter.page,
      pageSize: this.filter.pageSize
    };

    if (this.filter.jurisdictionId) payload.jurisdictionId = this.filter.jurisdictionId;
    if (this.filter.customerType) payload.customerType = this.filter.customerType;
    if (this.filter.tierCode) payload.tierCode = this.filter.tierCode;
    if (this.filter.isActive !== '') payload.isActive = this.filter.isActive;

    this.adminService.getTiers(payload).subscribe({
      next: (res: any) => {
          console.log(res);
        this.meta = res?.meta;
        this.items = res?.data || res || [];
        this.loading = false;
      },
      error: (err) => {
        this.error = err?.error?.message || 'Failed to load tiers.';
        this.loading = false;
      }
    });
  }

  createNew(): void {
    this.persistFilters();
    this.router.navigate(['/admin/compliance/tiers/new']);
  }

  edit(item: any): void {
    this.persistFilters();
    this.router.navigate(['/admin/compliance/tiers', item.id, 'edit']);
  }

  openLimits(item: any): void {
    this.persistFilters();
    this.router.navigate(['/admin/compliance/tiers', item.id, 'limits']);
  }

  copy(item: any): void {
    if (!item?.id) return;

    this.persistFilters();
    this.router.navigate(['/admin/compliance/tiers/new'], {
      queryParams: { copyFrom: item.id }
    });
  }

  resetFilters(): void {
    this.filter = { ...this.defaultFilter };
    this.clearPersistedFilters();
    this.load();
  }

  persistFilters(): void {
    sessionStorage.setItem(this.filterStorageKey, JSON.stringify(this.filter));
  }

  restoreFilters(): void {
    const raw = sessionStorage.getItem(this.filterStorageKey);
    if (!raw) return;

    try {
      const saved = JSON.parse(raw);

      this.filter = {
        ...this.defaultFilter,
        ...saved,
        page: Number(saved?.page || this.defaultFilter.page),
        pageSize: Number(saved?.pageSize || this.defaultFilter.pageSize)
      };
    } catch {
      this.filter = { ...this.defaultFilter };
    }
  }

  clearPersistedFilters(): void {
    sessionStorage.removeItem(this.filterStorageKey);
  }

  asYesNo(value: any): string {
    return value ? 'Yes' : 'No';
  }

  statusBadgeClass(value: any): string {
    return value ? 'text-bg-success' : 'text-bg-secondary';
  }

  getCustomerTypeLabel(value: any): string {
    return this.customerTypes.find(x => x.value === value)?.label || value || '-';
  }

  getJurisdictionLabel(item: any): string {
    if (item.jurisdictionCode && item.jurisdictionName) {
      return `${item.jurisdictionCode} - ${item.jurisdictionName}`;
    }

    if (item.jurisdictionCode) return item.jurisdictionCode;
    if (item.jurisdictionName) return item.jurisdictionName;

    const match = this.jurisdictions.find(x => x.id === item.jurisdictionId);
    if (match) return `${match.code} - ${match.name}`;

    return item.jurisdictionId || '-';
  }
}
